import { storeToRefs } from 'pinia';
import { UNPAID_LEVEL, UnpaidLevel } from '~/app/auth/enums/UnpaidLevel';

export interface UnpaidBannerVariant {
    level: UnpaidLevel;
    /** Background (gradient or flat tint) + optional border, per the handoff. */
    classes: string;
    /** L1 is a light tint with dark text; L2/L3 are dark gradients with white text. */
    dark: boolean;
    titleKey: string;
    titleSmKey: string;
    subtitleKey: string;
    ctaKey: string;
}

// Progressive banner tints (TDD-5600, "Unpaid New UI" handoff): light yellow (L1,
// dark text) → amber gradient (L2) → red gradient (L3) → dark red (L4). A confined
// L4 shows no banner (the blocking screen takes over): its banner only appears
// during a temporary unblock (TDD-6560).
const BANNER_VARIANTS: Record<Exclude<UnpaidLevel, typeof UNPAID_LEVEL.NONE>, UnpaidBannerVariant> = {
    [UNPAID_LEVEL.SOFT_REMINDER]: {
        level: UNPAID_LEVEL.SOFT_REMINDER,
        classes: 'bg-branding-lemon border-b border-branding-mid-yellow',
        dark: true,
        titleKey: 'tenancy.tenant.unpaid.banner.level_1.title',
        titleSmKey: 'tenancy.tenant.unpaid.banner.level_1.title_sm',
        subtitleKey: 'tenancy.tenant.unpaid.banner.level_1.subtitle',
        ctaKey: 'tenancy.tenant.unpaid.banner.level_1.cta',
    },
    [UNPAID_LEVEL.ATTENTION]: {
        level: UNPAID_LEVEL.ATTENTION,
        classes: 'bg-gradient-to-r from-amber-600 to-amber-700',
        dark: false,
        titleKey: 'tenancy.tenant.unpaid.banner.level_2.title',
        titleSmKey: 'tenancy.tenant.unpaid.banner.level_2.title_sm',
        subtitleKey: 'tenancy.tenant.unpaid.banner.level_2.subtitle',
        ctaKey: 'tenancy.tenant.unpaid.banner.level_2.cta',
    },
    [UNPAID_LEVEL.WARNING]: {
        level: UNPAID_LEVEL.WARNING,
        classes: 'bg-gradient-to-r from-red-500 to-red-700',
        dark: false,
        titleKey: 'tenancy.tenant.unpaid.banner.level_3.title',
        titleSmKey: 'tenancy.tenant.unpaid.banner.level_3.title_sm',
        subtitleKey: 'tenancy.tenant.unpaid.banner.level_3.subtitle',
        ctaKey: 'tenancy.tenant.unpaid.banner.level_3.cta',
    },
    [UNPAID_LEVEL.BLOCKED]: {
        level: UNPAID_LEVEL.BLOCKED,
        classes: 'bg-gradient-to-r from-red-700 to-red-900',
        dark: false,
        titleKey: 'tenancy.tenant.unpaid.banner.level_4_unblocked.title',
        titleSmKey: 'tenancy.tenant.unpaid.banner.level_4_unblocked.title_sm',
        subtitleKey: 'tenancy.tenant.unpaid.banner.level_4_unblocked.subtitle',
        ctaKey: 'tenancy.tenant.unpaid.banner.level_4_unblocked.cta',
    },
};

const UNPAID_SETTLER_ROLES = ['admin', 'super-admin'];

/**
 * Routes that stay reachable while confined at level 4 (TDD-5603): the invoices
 * screen itself, the auth flows (logout, tenant switch, inaccessible pages) and
 * the tenant switcher. Shared by unpaid-blocked.global.ts (navigations) and
 * useGetGlobalRedirection (initial load, where the middleware runs too early:
 * the tenant meta is not fetched yet).
 */
export const isUnpaidConfinementExempt = (path: string): boolean => path.startsWith('/settings/invoices') || path.startsWith('/auth') || path.startsWith('/switch-tenant');

/**
 * Single source of truth for the tenant's unpaid status (TDD-5599).
 * The level comes from the backend; the business rules moving a tenant
 * between levels are out of the frontend's scope.
 */
export const useUnpaidStatus = () => {
    const { tenant, authed } = storeToRefs(useAuthStore());

    // Local test forcing (see useUnpaidDevForce): overrides the backend level,
    // with demo count/total when the tenant has no real unpaid data.
    const forcedLevel = getForcedUnpaidLevel();

    const level = computed<UnpaidLevel>(() => forcedLevel ?? tenant.value?.unpaidLevel ?? UNPAID_LEVEL.NONE);
    const invoicesCount = computed<number>(() => {
        const count = tenant.value?.unpaidInvoicesCount ?? 0;
        if (forcedLevel && count === 0) return forcedLevel === UNPAID_LEVEL.SOFT_REMINDER ? 1 : 3;

        return count;
    });
    const totalDue = computed<number>(() => {
        const total = tenant.value?.unpaidTotalDue ?? 0;
        if (forcedLevel && total === 0) return invoicesCount.value * 14900;

        return total;
    });
    // Only admins can settle (TDD-6833): banners and nav badges are theirs. The level 4 block applies to everyone.
    const canSettle = computed<boolean>(() => UNPAID_SETTLER_ROLES.includes(authed.value?.role ?? ''));
    const hasUnpaidInvoices = computed<boolean>(() => canSettle.value && level.value > UNPAID_LEVEL.NONE);
    const unblockedUntil = computed(() => tenant.value?.unpaidUnblockedUntil ?? null);
    // A forced level 4 always confines (preview); a real one only does while no
    // temporary unblock is in progress (tenant.isUnpaidBlocked, TDD-5631).
    const isBlocked = computed<boolean>(() => (forcedLevel !== null ? forcedLevel === UNPAID_LEVEL.BLOCKED : tenant.value?.isUnpaidBlocked ?? false));
    // An unconfined L4 without unblock date is impersonating staff: no "unblocked until" banner to show.
    const showBanner = computed<boolean>(() => hasUnpaidInvoices.value && !isBlocked.value && (level.value !== UNPAID_LEVEL.BLOCKED || !!unblockedUntil.value));

    const bannerVariant = computed<UnpaidBannerVariant | null>(() => {
        if (!showBanner.value) return null;

        return BANNER_VARIANTS[level.value as keyof typeof BANNER_VARIANTS] ?? null;
    });

    return {
        level,
        invoicesCount,
        totalDue,
        hasUnpaidInvoices,
        isBlocked,
        unblockedUntil,
        showBanner,
        bannerVariant,
    };
};
